import { z } from 'zod';
/**
 * Testphase (KON-177, Verkaufsprozess KON-175). Ein Testmandant ist ein gewöhnlicher Mandant mit Status `trial` und einem Ablaufzeitpunkt in `valid_until`.
 * Bei der Umwandlung in einen bezahlten Mandanten wird der Status `active`, alles andere bleibt.
 */
/** Dauer der Testphase: ein Monat (Entscheidung D-9 vom 15.10.2026, auf einen Monat verlängert am 17.10.2026, weil das Paket nun jährlich abgerechnet wird) */
export const TRIAL_DAYS = 30;
/** Schonfrist nach dem Ende: Der Zugang ist gesperrt, bestellen geht noch, danach werden die Daten gelöscht (Entscheidung D-10) */
export const TRIAL_GRACE_DAYS = 3;
/** Erinnerungen per E-Mail: so viele Tage vor dem Ende */
export const TRIAL_REMINDER_DAYS = [7, 3, 1];
/** Paket der Testphase: Premium mit allen Modulen des Pakets, ein Benutzer (Entscheidung D-9, am 28.10.2026 von Standard auf Premium geändert, KON-257) */
export const TRIAL_PLAN = 'premium';
/**
 * Grenzen der Testphase (KON-178). Die Werte sind Vorschläge und stehen hier an einer Stelle, damit sie sich ohne Eingriff in den Code ändern lassen.
 * Nicht hier stehen der Versand an Dritte (in der Testphase gesperrt) und die Schlüssel für Webseiten (legt nur der Betreiber an, also entfällt auch der Webchat).
 */
export const TRIAL_LIMITS = {
    /** Benutzerplätze (`tenants.max_users`): wie beim bezahlten Paket nur der Inhaber, weitere Plätze gibt es erst mit der Bestellung (Zusatzbuchung, siehe INCLUDED_USERS in billing.ts) */
    users: 1,
    /** Speicher insgesamt in Byte (`tenants.settings.storageQuotaBytes`), 100 MB */
    storageBytes: 100 * 1024 * 1024,
    productImages: 10,
    vehicleImages: 5,
    employeePhotos: 5,
};
/** Kennzeichnung auf Belegen, die in der Testphase entstehen (Entscheidung D-12) */
export const TRIAL_DOCUMENT_NOTE = 'Testbeleg, keine Rechnung';
const DAY_MS = 24 * 3600_000;
/** Volle Tage bis zum Ende, aufgerundet: ab dem letzten Augenblick des letzten Tages zeigt die Oberfläche „noch 1 Tag“, nach dem Ablauf 0 */
export function trialDaysLeft(validUntil, now = new Date()) {
    if (!validUntil)
        return 0;
    const end = typeof validUntil === 'string' ? new Date(validUntil).getTime() : validUntil.getTime();
    return Math.max(0, Math.ceil((end - now.getTime()) / DAY_MS));
}
/** Ende der Testphase, wenn sie jetzt beginnt */
export function trialEnd(now = new Date()) {
    return new Date(now.getTime() + TRIAL_DAYS * DAY_MS);
}
// --- Testanmeldung auf der Webseite (KON-176, Schutzstufen KON-185) ---------------------------------------------------------------------------------
/** Werte der Testanmeldung. Der Server setzt sie durch, die Webseite zeigt sie an. */
export const TRIAL_SIGNUP = {
    /** Stellen der Bestätigungsnummer */
    codeDigits: 6,
    /** So lange gilt eine Bestätigungsnummer, in Minuten */
    codeMinutes: 15,
    /** Falsche Eingaben, danach ist die Anmeldung ungültig */
    maxAttempts: 5,
    /** Neue Bestätigungsnummer: höchstens so oft pro Stunde, mit mindestens so vielen Sekunden Abstand */
    maxSendsPerHour: 3,
    minSendGapSeconds: 60,
    /** Mindestzeit zwischen Laden der Seite und Absenden. Ein Programm füllt schneller aus. */
    minFillSeconds: 4,
    /** So lange gilt das Formular-Token, in Stunden */
    formHours: 2,
    /** Unbestätigte Anmeldungen werden nach so vielen Stunden gelöscht */
    keepUnconfirmedHours: 24,
};
/** Gleiche Schreibweise für Telefonnummern, damit sich eine Nummer nicht mit anderen Leerzeichen oder Vorwahlen umgehen lässt. Ohne Landesvorwahl gilt Deutschland. */
export function normalizeSignupPhone(raw) {
    let s = raw.trim().replace(/^(\+\d{1,3})\s*\(0\)/, '$1');
    s = s.replace(/[\s\-().\/]/g, '');
    if (s.startsWith('00'))
        s = `+${s.slice(2)}`;
    else if (s.startsWith('0'))
        s = `+49${s.slice(1)}`;
    return /^\+[1-9]\d{6,14}$/.test(s) ? s : null;
}
/** Name und Firma: keine Links und kein HTML, damit die Anmeldung keine Werbung oder Schadcode transportiert */
const plainText = (label, min, max) => z
    .string()
    .trim()
    .min(min, `${label} fehlt`)
    .max(max, `${label} ist zu lang`)
    .refine((v) => !/https?:|www\.|[<>]/i.test(v), `${label}: Bitte keine Links oder Sonderzeichen wie < und >`);
export const TRIAL_INDUSTRIES = ['Gastronomie', 'Chauffeur-Service', 'Weinhandel', 'Andere'];
export const trialSignupInput = z.object({
    name: plainText('Name', 2, 120),
    email: z.string().trim().toLowerCase().pipe(z.email('Bitte prüfen Sie Ihre E-Mail-Adresse')).refine((v) => v.length <= 200, 'E-Mail-Adresse ist zu lang'),
    phone: z
        .string()
        .trim()
        .min(1, 'Telefonnummer fehlt')
        .max(40)
        .transform((v, ctx) => {
        const n = normalizeSignupPhone(v);
        if (!n)
            ctx.addIssue({ code: 'custom', message: 'Bitte prüfen Sie die Telefonnummer, zum Beispiel +49 151 1234567' });
        return n ?? '';
    }),
    company: plainText('Firma', 2, 120),
    industry: z.enum(TRIAL_INDUSTRIES).optional(),
    consent: z.literal(true, { error: 'Bitte stimmen Sie den Datenschutzhinweisen zu' }),
    /** Verstecktes Feld: Menschen lassen es leer, Programme füllen es aus */
    website: z.string().max(200).optional(),
    /** Token aus `GET /trial/form`, damit die Mindestzeit nicht zu umgehen ist */
    formToken: z.string().min(10).max(300),
    /** Lösung der Rechenaufgabe im Browser, nur wenn der Server sie verlangt */
    pow: z.string().max(40).optional(),
});
export const trialConfirmInput = z.object({
    id: z.uuid('Ungültige Anmeldung'),
    code: z.string().trim().regex(/^\d{6}$/, 'Die Bestätigungsnummer hat sechs Ziffern'),
});
//# sourceMappingURL=trial.js.map